import React, { CSSProperties, useEffect, useState } from "react"
import {
  createSearchParams,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { computed } from "mobx"
import { observer } from "mobx-react"
import {
  Button,
  Dropdown,
  Flex,
  Layout,
  Radio,
  RadioChangeEvent,
  theme,
} from "antd"
import { SafetyOutlined, UserOutlined } from "@ant-design/icons"
import classNames from "classnames"

import { authStore } from "@src/app/authStore"
import AsideMenu from "@src/app/components/AsideMenu"
import { Logo } from "@src/app/components/Logo"
import {
  DashboardLayoutProvider,
  useDashboardContainerWidth,
} from "@src/app/layouts/dashboardLayout"
import { paths } from "@src/app/paths"
import uiStore from "@src/app/uiStore"
import { MassPayoutPageVariants } from "@src/pages/massPayouts/constants"
import { MerchantResponse } from "@src/shared/api/generated"
import { isSandbox } from "@src/shared/utils/isSandbox"
import Onboarding from "@src/widgets/onboarding/ui"
import css from "./index.module.css"

const { Content, Footer, Header } = Layout

const { Button: RadioButton, Group } = Radio

const SIDEBAR_WIDTH = 280
const TOP_NAV_BREAKPOINT = 1140
const TOP_NAV_HEIGHT = 72

export const Main = observer(() => {
  const { pathname } = useLocation()
  const { isTransferioTheme } = uiStore()

  const { token } = theme.useToken()

  const { logout, merchant } = authStore

  const navigate = useNavigate()

  const { kyb_level } = merchant ?? ({} as MerchantResponse)

  const isPayoutsPage = pathname === paths.massPayouts

  const [searchParams] = useSearchParams()

  const massPayoutsView = searchParams.get("view")

  const email = computed(() => {
    return merchant?.submerchant ? merchant.submerchant.email : merchant?.email
  }).get()

  const handlePayoutTypeChange = (e: RadioChangeEvent) => {
    navigate({
      pathname: paths.massPayouts,
      search: `?${createSearchParams({ view: e.target.value })}`,
    })
  }

  const sandbox = isSandbox()
  const { ref: contentRef, width: contentWidth } = useDashboardContainerWidth()
  const [isTopNavigation, setIsTopNavigation] = useState(
    () => window.innerWidth < TOP_NAV_BREAKPOINT,
  )

  useEffect(() => {
    const updateNavigationMode = () => {
      setIsTopNavigation(window.innerWidth < TOP_NAV_BREAKPOINT)
    }

    updateNavigationMode()
    window.addEventListener("resize", updateNavigationMode)

    return () => window.removeEventListener("resize", updateNavigationMode)
  }, [])

  return (
    <Layout hasSider>
      <Flex
        align="center"
        className={classNames(css.menu, {
          [css.menuSandbox as string]: sandbox,
          [css.menuTop as string]: isTopNavigation,
        })}
        vertical={!isTopNavigation}
      >
        {!isTransferioTheme && (
          <div className={css.logoWrap}>
            <Logo size={"full"} />
          </div>
        )}
        <AsideMenu isHorizontal={isTopNavigation} />
      </Flex>

      <div
        className={css.shell}
        style={
          {
            "--dashboard-sidebar-width": isTopNavigation
              ? "0px"
              : `${SIDEBAR_WIDTH}px`,
            "--dashboard-top-nav-height": isTopNavigation
              ? `${TOP_NAV_HEIGHT}px`
              : "0px",
          } as CSSProperties
        }
      >
        <DashboardLayoutProvider width={contentWidth}>
          <Layout
            className="site-layout"
            style={{
              gap: "var(--dashboard-shell-gap)",
              minWidth: 0,
              paddingTop: isTopNavigation
                ? sandbox
                  ? "calc(38px + var(--dashboard-top-nav-height) + var(--dashboard-shell-gap))"
                  : "calc(var(--dashboard-top-nav-height) + var(--dashboard-shell-gap))"
                : sandbox
                  ? 44
                  : "var(--dashboard-shell-gap)",
            }}
          >
            <Header
              style={{
                alignItems: "center",
                background: token.colorBgLayout,
                display: "flex",
                height: "var(--dashboard-header-height)",
                justifyContent: isPayoutsPage ? "space-between" : "flex-end",
                padding: "var(--dashboard-header-padding)",
                textAlign: "right",
              }}
            >
              {isPayoutsPage && (
                <Group
                  buttonStyle="solid"
                  defaultValue={MassPayoutPageVariants.Cards}
                  onChange={handlePayoutTypeChange}
                  size="small"
                  value={massPayoutsView}
                >
                  <RadioButton
                    id="GENERATED_ID_MAIN_RADIOBUTTON_CSV_FILE_1"
                    value={String(MassPayoutPageVariants.Cards)}
                  >
                    Cards
                  </RadioButton>
                  <RadioButton
                    id="GENERATED_ID_MAIN_RADIOBUTTON_MANUAL_PAYOUTS_1"
                    value={String(MassPayoutPageVariants.Crypto)}
                  >
                    Crypto
                  </RadioButton>
                </Group>
              )}
              <div className={css.buttonsWrapper}>
                <Button
                  icon={<SafetyOutlined />}
                  id="GENERATED_ID_MAIN_BUTTON_VOID_1"
                  onClick={() => navigate(paths.kyb)}
                >
                  {`KYB Lv. ${kyb_level}`}
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "exit",
                        label: (
                          <a
                            onClick={() =>
                              logout().then(() => navigate(paths.auth))
                            }
                          >
                            Exit
                          </a>
                        ),
                      },
                    ],
                  }}
                >
                  <Button
                    icon={<UserOutlined />}
                    id="GENERATED_ID_MAIN_BUTTON_VOID_2"
                  >
                    {email}
                  </Button>
                </Dropdown>
              </div>
            </Header>

            <Content className={css.content} ref={contentRef}>
              <Outlet />
            </Content>

            <Footer className={css.footer}>
              {!isTransferioTheme && (
                <span>{`© ${new Date().getFullYear()} Cryptix`}</span>
              )}
              <div style={{ marginLeft: "auto" }}>
                {!isTransferioTheme && (
                  <>
                    <a href="https://www.cryptix.io/about-us" target={"_blank"}>
                      About
                    </a>
                    <a
                      href="https://www.cryptix.io/service-fees"
                      target={"_blank"}
                    >
                      Service fees
                    </a>
                  </>
                )}
                <a
                  href={
                    isTransferioTheme
                      ? "mailto:support@transfer.io"
                      : "mailto:support@cryptix.io"
                  }
                >
                  Support
                </a>
                <a
                  href={
                    isTransferioTheme
                      ? "https://t.me/transferio_chat"
                      : "https://t.me/cryptix_chat"
                  }
                  target={"_blank"}
                >
                  Contact Us
                </a>
                <a
                  href={
                    isTransferioTheme
                      ? "https://developers.transferio.io"
                      : "https://developers.cryptix.io/"
                  }
                  target={"_blank"}
                >
                  API Documentation
                </a>
              </div>
            </Footer>
          </Layout>
        </DashboardLayoutProvider>
      </div>

      {!isTransferioTheme && <Onboarding />}
    </Layout>
  )
})
